<div class="min-h-screen bg-gray-50 py-8">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="text-center mb-8">
      <h1 class="text-3xl font-bold text-gray-900 mb-2">
        Design-to-Code Figma Token Validator
      </h1>
      <p class="text-gray-600">
        Validate your Figma project tokens and the corresponding components step
        by step. <br />
        <span class="font-bold"
          >Make sure all steps are passing the verifications before using
          DTC</span
        >
        <br />
        <span
          >For more details on how to use it, check the
          <a
            class="text-blue-500 underline"
            target="_blank"
            href="https://gl.fromscratch.gr/team/products/design-to-code/design-to-code-guard/-/blob/main/README.md?ref_type=heads"
            >documentation</a
          ></span
        >
      </p>
    </div>

    <!-- Progress Bar -->
    <div class="mb-8">
      <div class="flex items-center justify-center mb-4">
        <div class="flex items-center space-x-4">
          @for (step of steps; track step.title; let i = $index) {
          <div class="flex items-center" [class.opacity-50]="i > currentStep()">
            <div
              class="flex items-center text-white justify-center w-10 h-10 rounded-full text-sm font-medium transition-colors duration-200"
              [ngClass]="{
                'bg-blue-600': i === currentStep(),
                'bg-green-600': i < currentStep(),
                'bg-gray-200 text-gray-500': i > currentStep()
              }"
            >
              @if (i > currentStep() || i === currentStep()) {
              <span>{{ i + 1 }}</span>
              } @if (i < currentStep()) {
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              }
            </div>
            @if (i < steps.length - 1) {
            <div
              class="w-16 h-1 mx-2 transition-colors duration-200"
              [ngClass]="{
                'bg-green-600': i < currentStep(),
                'bg-gray-200': i >= currentStep()
              }"
            ></div>
            }
          </div>
          }
        </div>
      </div>
      <div class="text-center">
        <h2 class="text-xl font-semibold text-gray-900">
          {{ steps[currentStep()].title }}
        </h2>
        <p class="text-gray-600 mt-1">{{ steps[currentStep()].description }}</p>
      </div>
    </div>

    <!-- Wizard Content -->
    <form [formGroup]="wizardForm" class="bg-white rounded-lg shadow-lg p-8">
      <!-- Step 1: Figma Credentials -->
      @if (currentStep() === 0) {
      <div formGroupName="credentials" class="space-y-6">
        <div>
          <label
            for="accessToken"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Figma Access Token *
          </label>
          <input
            type="password"
            id="accessToken"
            formControlName="figmaAccessToken"
            placeholder="Enter your Figma access token"
            class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
            [class.border-red-300]="
              isFieldInvalid('credentials.figmaAccessToken')
            "
            [class.border-gray-300]="
              !isFieldInvalid('credentials.figmaAccessToken')
            "
          />
          @if (isFieldInvalid('credentials.figmaAccessToken')) {
          <div class="text-red-600 text-sm mt-1">
            Figma access token is required
          </div>
          }
          <p class="text-sm text-gray-500 mt-1">
            You can generate this in your Figma account settings
          </p>
        </div>

        <div>
          <label
            for="projectId"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Figma Project ID *
          </label>
          <input
            type="text"
            id="projectId"
            formControlName="figmaProjectId"
            placeholder="Enter your Figma project ID"
            class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
            [class.border-red-300]="
              isFieldInvalid('credentials.figmaProjectId')
            "
            [class.border-gray-300]="
              !isFieldInvalid('credentials.figmaProjectId')
            "
          />
          @if (isFieldInvalid('credentials.figmaProjectId')) {
          <div class="text-red-600 text-sm mt-1">
            Figma project ID is required
          </div>
          }
          <p class="text-sm text-gray-500 mt-1">
            Found in the URL of your Figma file
          </p>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg">
          <button
            type="button"
            (click)="verifyFigmaAccess()"
            [disabled]="isVerifying() || !canVerifyFigmaAccess()"
            class="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            @if (isVerifying()) {
            <svg
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            }
            <span>{{
              isVerifying() ? "Verifying..." : "Verify Figma Access"
            }}</span>
          </button>
        </div>

        @if (figmaAccessVerificationResult()) {
        <div
          class="rounded-lg p-4"
          [ngClass]="{
            'bg-green-50 border border-green-200':
              figmaAccessVerificationResult()?.success,
            'bg-red-50 border border-red-200':
              !figmaAccessVerificationResult()?.success
          }"
        >
          <div class="flex items-center">
            @if (figmaAccessVerificationResult()?.success) {
            <svg
              class="w-5 h-5 text-green-600 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              ></path>
            </svg>
            } @if (!figmaAccessVerificationResult()?.success) {
            <svg
              class="w-5 h-5 text-red-600 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              ></path>
            </svg>
            }

            <span
              class="font-medium"
              [ngClass]="{
                'text-green-800': figmaAccessVerificationResult()?.success,
                'text-red-800': !figmaAccessVerificationResult()?.success
              }"
            >
              @for (message of
              figmaAccessVerificationResult()?.response?.messages; track
              message) {
              {{ message }}
              }
            </span>
            @if (!figmaAccessVerificationResult()?.success) {
            <app-info-popover
              class="ml-2"
              title="Figma Access Error"
              description="Please make sure you use the correct project ID as shown in the image."
              imageSrc="access-error.png"
              position="right"
            >
            </app-info-popover>
            }
          </div>
        </div>
        }
      </div>
      }

      <!-- Step 2: Token Node IDs -->
      @if (currentStep() === 1) {
      <div formGroupName="tokens" class="space-y-6">
        <div>
          <label
            for="foundationTokenNodeId"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Foundation Token Node ID *
          </label>
          <input
            type="text"
            id="foundationTokenNodeId"
            formControlName="foundationTokenNodeId"
            placeholder="Enter foundation token node ID"
            class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
            [class.border-red-300]="
              isFieldInvalid('tokens.foundationTokenNodeId')
            "
            [class.border-gray-300]="
              !isFieldInvalid('tokens.foundationTokenNodeId')
            "
          />
          @if (isFieldInvalid('tokens.foundationTokenNodeId')) {
          <div class="text-red-600 text-sm mt-1">
            Foundation token node ID is required
          </div>
          }
        </div>

        <div>
          <label
            for="colorTokenNodeId"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Color Token Node ID *
          </label>
          <input
            type="text"
            id="colorTokenNodeId"
            formControlName="colorTokenNodeId"
            placeholder="Enter color token node ID"
            class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
            [class.border-red-300]="isFieldInvalid('tokens.colorTokenNodeId')"
            [class.border-gray-300]="!isFieldInvalid('tokens.colorTokenNodeId')"
          />
          @if (isFieldInvalid('tokens.colorTokenNodeId')) {
          <div class="text-red-600 text-sm mt-1">
            Color token node ID is required
          </div>
          }
        </div>

        <!-- Verification Button -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <button
            type="button"
            (click)="verifyTokenNodes()"
            [disabled]="isVerifying() || !canVerifyTokens()"
            class="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            @if (isVerifying()) {
            <svg
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            }
            <span>{{
              isVerifying() ? "Verifying..." : "Verify Token Structure"
            }}</span>
          </button>
        </div>

        <!-- Verification Result -->
        @if (nodesVerificationResult()) {
        <div
          class="rounded-lg p-4"
          [ngClass]="{
            'bg-green-50 border border-green-200':
              nodesVerificationResult()?.success,
            'bg-red-50 border border-red-200':
              !nodesVerificationResult()?.success
          }"
        >
          <div class="flex items-center">
            @if (nodesVerificationResult()?.success) {
            <svg
              class="w-5 h-5 text-green-600 mr-2 self-start"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              ></path>
            </svg>
            } @if (!nodesVerificationResult()?.success) {
            <svg
              class="w-5 h-5 text-red-600 mr-2 self-start"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              ></path>
            </svg>
            }

            <div
              class="font-medium"
              [ngClass]="{
                'text-green-800': nodesVerificationResult()?.success,
                'text-red-800': !nodesVerificationResult()?.success
              }"
            >
              @if (nodesVerificationResult()?.response?.messages !== null) {
              @for (message of nodesVerificationResult()?.response?.messages;
              track message) {
              {{ message }}
              } } @if (nodesVerificationResult()?.response?.structure &&
              nodesVerificationResult()?.response?.structure?.length) {
              <div class="flex">
                <div class="font-bold">Tokens are not structured properly</div>
                <app-info-popover
                  class="ml-2"
                  title="Structure Problems"
                  description="Token names must use only alphanumeric characters, separated by hyphens (-). Each token name must be followed by a single colon (:) separating it from its value."
                  position="right"
                >
                </app-info-popover>
              </div>
              <ul>
                @for (issue of nodesVerificationResult()?.response?.structure;
                track issue) {
                <li>- {{ issue }}</li>
                }
              </ul>
              } @if (nodesVerificationResult()?.response?.missing &&
              nodesVerificationResult()?.response?.missing?.length) {
              <div class="flex">
                <div class="font-bold">Missing Tokens</div>
                <app-info-popover
                  class="ml-2"
                  title="Some required tokens are missing"
                  description="The following tokens are required for a well-structure implementation: spacing, border-radius, border-width, line-height, font-size, font-weight, font-family"
                  position="right"
                >
                </app-info-popover>
              </div>
              <ul>
                @for (issue of nodesVerificationResult()?.response?.missing;
                track issue) {
                <li>- {{ issue }}</li>
                }
              </ul>
              } @if (nodesVerificationResult()?.response?.invalid &&
              nodesVerificationResult()?.response?.invalid?.length) {
              <div class="flex">
                <div class="font-bold">Invalid Tokens</div>
                <app-info-popover
                  class="ml-2"
                  title="Tokens are not set correctly"
                  description="Distances must be specified as positive numeric values with a px suffix. Colors must be defined using either valid hex codes prefixed with # or valid rgba(r, g, b, a) formats"
                  position="right"
                >
                </app-info-popover>
              </div>
              <ul>
                @for (issue of nodesVerificationResult()?.response?.invalid;
                track issue) {
                <li>- {{ issue }}</li>
                }
              </ul>
              }
            </div>
            @if (!nodesVerificationResult()?.success &&
            !nodesVerificationResult()?.response?.structure) {
            <app-info-popover
              class="ml-2"
              title="Node Structure Error"
              description="Please make sure the nodes provided include a SINGLE node containing the desired tokens"
              imageSrc="high-level-structure-error.png"
              position="right"
            >
            </app-info-popover>
            }
          </div>
        </div>
        } @if (nodesVerificationResult()?.response?.groups &&
        nodesVerificationResult()?.response?.groups?.length) {
        <div class="rounded-lg p-4 bg-yellow-50 border border-yellow-200">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 text-yellow-600 mr-2 self-start"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <div class="font-medium text-yellow-800">
              <div class="flex">
                <div class="font-bold">Warnings</div>
                <app-info-popover
                  class="ml-2"
                  title="Ungrouped Tokens Found"
                  description="Some tokens are not assigned to any style group. While this may be intentional, please verify that it is not due to a typo or misconfiguration."
                  position="right"
                >
                </app-info-popover>
              </div>
              <ul>
                @for (warning of nodesVerificationResult()?.response?.groups;
                track warning) {
                <li>- {{ warning }}</li>
                }
              </ul>
            </div>
          </div>
        </div>
        }
      </div>
      }

      <!-- Step 3: Component Validation -->
      @if (currentStep() === 2) {
      <div class="space-y-6">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 text-blue-600 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span class="text-blue-800 font-medium">
              Add component node ID to validate its token usage
            </span>
          </div>
        </div>

        <!-- Component Node IDs -->
        <div formGroupName="componentNode">
          <label class="block text-sm font-medium text-gray-700 mb-3">
            Component Node IDs
          </label>

          <div class="space-y-3">
            <div class="flex items-start space-x-2">
              <div class="flex-1">
                <input
                  type="text"
                  formControlName="nodeId"
                  placeholder="Enter component node ID"
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                  [class.border-red-300]="
                    isFieldInvalid('componentNode.nodeId')
                  "
                  [class.border-gray-300]="
                    !isFieldInvalid('componentNode.nodeId')
                  "
                />
                @if (isFieldInvalid('componentNode.nodeId') ) {
                <div class="text-red-600 text-sm mt-1">
                  Component node ID is required
                </div>
                }
              </div>
              <button
                type="button"
                (click)="validateComponent()"
                [disabled]="
                  isFieldInvalid('componentNode.nodeId') ||
                  componentValidations()?.isValidating
                "
                class="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-1"
              >
                @if (componentValidations()?.isValidating) {
                <svg
                  class="animate-spin w-4 h-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                }
                <span>{{
                  componentValidations()?.isValidating
                    ? "Validating"
                    : "Validate"
                }}</span>
              </button>
            </div>
          </div>

          <!-- Validation Results -->
          <div class="mt-3">
            @if (componentValidations() && componentValidations().result) {
            <div
              class="rounded-lg p-3 text-sm"
              [ngClass]="{
                'bg-green-50 border border-green-200 text-green-800':
                  componentValidations().result?.success,
                'bg-red-50 border border-red-200 text-red-800':
                  !componentValidations().result?.success,
              }"
            >
              <div class="flex items-center">
                @if (componentValidations().result?.success) {
                <svg
                  class="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                } @if (!componentValidations().result?.success) {
                <svg
                  class="w-4 h-4 mr-2 self-start"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                }
                <div>
                  @if (!componentValidations().result?.success &&
                  componentValidations().result?.errorMessages) {
                  <span class="font-bold">Unable to identify some tokens</span>
                  <ul>
                    @for (message of
                    componentValidations().result?.errorMessages; track message)
                    {
                    <li>{{ message }}</li>
                    }
                  </ul>
                  } @else { Component tokens are correctly configured and follow
                  design system guidelines. }
                </div>
              </div>
            </div>
            }
          </div>
        </div>
      </div>
      }

      <!-- Navigation Buttons -->
      <div
        class="flex justify-between items-center mt-8 pt-6 border-t border-gray-200"
      >
        @if (currentStep() > 0) {
        <button
          type="button"
          (click)="previousStep()"
          class="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 flex items-center space-x-2"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            ></path>
          </svg>
          <span>Previous</span>
        </button>
        } @if (currentStep() === 0) {
        <div></div>
        } @if (currentStep() < maxSteps - 1) {

        <button
          type="button"
          (click)="nextStep()"
          [disabled]="!canProceedToNextStep()"
          class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
        >
          <span>{{
            currentStep() === maxSteps - 2 ? "Start Validation" : "Next"
          }}</span>
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
        </button>
        } @if (currentStep() === maxSteps - 1) {
        <div class="text-sm text-gray-600">
          Final step - Add and validate your components
        </div>
        }
      </div>
    </form>
  </div>
</div>
